/**
 * The M365 board's visual grammar, in one place so the tiles, the summary
 * strip, and the incident table can never disagree.
 *
 * The backend already normalises Graph's `status` enum into a small `state`
 * union and a display label (`nova.m365.service`); this module only maps that
 * state onto design-system tones and formats timestamps.
 */

import type { M365Incident, M365ServiceState } from "@/lib/api";

/** Normalised service state, as the backend defines it. */
export type M365State = M365ServiceState["state"];

/** Tone shared by `Badge`, `Statistic`, and `Card` — their unions overlap here. */
export type StatusTone = "success" | "warning" | "danger" | "neutral";

/**
 * Subset of the `Timeline` item tones this board uses for a deadline marker.
 * Declared as a literal union rather than imported so the values are visible
 * here; `Timeline` accepts it structurally.
 */
export type DeadlineTone = "danger" | "warning" | "accent";

/**
 * A Message Center deadline this close needs an owner now. Two weeks is chosen
 * to be longer than a sprint, so a post surfaces while there is still time to
 * do something about it rather than on the day it expires.
 */
export const DEADLINE_SOON_DAYS = 14;

/** Finance-palette tone used by `StatisticBar`. */
export type StripTone = "positive" | "caution" | "negative" | "accent";

export const STATE_TONE: Record<M365State, StatusTone> = {
  operational: "success",
  degraded: "warning",
  investigating: "warning",
  restoring: "warning",
  interruption: "danger",
  // Restored means Microsoft has closed it out — good news, but not the same
  // as "nothing happened here", so it stays visually distinct from operational.
  restored: "success",
  unknown: "neutral",
};

/** Order used wherever states are listed, worst first — problems lead. */
export const STATE_ORDER: readonly M365State[] = [
  "interruption",
  "degraded",
  "investigating",
  "restoring",
  "restored",
  "operational",
  "unknown",
];

/**
 * The labels the summary strip prints, in one place because two components now
 * depend on them agreeing: the strip renders them, and `M365ErrorsAlert` names
 * them when a partial read makes them a lower bound. A label edited in only one
 * of the two would have the alert invalidating a figure that is not on screen.
 */
export const SUMMARY_LABELS = {
  servicesOperational: "Services operational",
  activeIncidents: "Active incidents",
  activeAdvisories: "Active advisories",
  noStatus: "No status",
  resolved: (windowDays: number) => `Resolved (${windowDays}d)`,
} as const;

/** Sort comparator putting the services that need attention at the top. */
export function bySeverity(a: M365ServiceState, b: M365ServiceState): number {
  const rank = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
  return rank !== 0 ? rank : a.service.localeCompare(b.service);
}

/** An incident is louder than an advisory — Graph's own distinction. */
export function classificationTone(classification: string): "danger" | "info" {
  return classification.toLowerCase() === "incident" ? "danger" : "info";
}

/**
 * Tone for a Message Center post's severity. Microsoft's scale, not ours, so an
 * unrecognised value falls through to neutral instead of being guessed at — the
 * same rule the backend applies to Graph's status enums.
 */
export function messageSeverityTone(severity: string): StatusTone {
  switch (severity.toLowerCase()) {
    case "critical":
      return "danger";
    case "high":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * How urgent a Message Center deadline is.
 *
 * Returns `null` when Microsoft named no deadline, which is a different fact
 * from "due at some unknown time": most posts require nothing of the tenant.
 * An unreadable date lands on `null` too — the backend parses these into real
 * datetimes before they leave it, so that branch only fires if that contract
 * breaks, and quietly grading such a post "later" would be the worse failure.
 * A deadline already past is the loudest band, because a missed Message Center
 * deadline is the operational pain this panel exists to prevent — the tenant
 * does not get a second notice.
 */
export function deadlineUrgency(
  iso: string | null | undefined,
  now: number = Date.now(),
): "overdue" | "soon" | "later" | null {
  if (!iso) {
    return null;
  }
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) {
    return null;
  }
  if (due < now) {
    return "overdue";
  }
  return due - now <= DEADLINE_SOON_DAYS * 86_400_000 ? "soon" : "later";
}

/** Marker tone per deadline band — status colours, so overdue reads as broken. */
export const DEADLINE_TONE: Record<"overdue" | "soon" | "later", DeadlineTone> = {
  overdue: "danger",
  soon: "warning",
  later: "accent",
};

/**
 * A deadline as prose relative to now: "5 days overdue", "in 12 days", "today".
 * Never a bare date — the question is how long there is left, and a reader
 * should not have to do the arithmetic on a deadline they might miss.
 */
export function formatDeadline(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) {
    return "—";
  }
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) {
    return "—";
  }
  const days = Math.round((due - now) / 86_400_000);
  if (days === 0) {
    // Rounding to the day would print "due today" for a deadline that passed
    // three hours ago, contradicting the danger marker beside it.
    return due < now ? "overdue today" : "due today";
  }
  const magnitude = Math.abs(days);
  const unit = magnitude === 1 ? "day" : "days";
  return days < 0 ? `${magnitude} ${unit} overdue` : `in ${magnitude} ${unit}`;
}

/**
 * Turn Graph's camelCase issue status ("serviceRestored") into prose
 * ("Service restored"). The backend passes the raw enum through deliberately —
 * it is Microsoft's vocabulary, so only the presentation is ours.
 */
export function humanizeIssueStatus(status: string): string {
  if (!status) {
    return "—";
  }
  const spaced = status.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Format a UTC ISO timestamp as a short local date + time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A whole-minute duration as prose — the unit the backend sends MTTR in.
 *
 * Null renders as an em dash, deliberately: a time-to-restore NOVA could not
 * compute is unknown, and "0 min" would read as Microsoft having fixed the
 * thing instantly. The two must never look alike (§5, "No data ≠ zero").
 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
    return "—";
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainder = minutes % 60;
    return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days} d ${remainingHours} h` : `${days} d`;
}

/**
 * How long an issue has run: start → end, or start → now while it is open.
 * Returns "—" when Microsoft gave us no start time rather than guessing one.
 */
export function formatDuration(
  startIso: string | null | undefined,
  endIso?: string | null,
  now: number = Date.now(),
): string {
  if (!startIso) {
    return "—";
  }
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) {
    return "—";
  }
  const end = endIso ? new Date(endIso).getTime() : now;
  return formatMinutes(Math.max(0, Math.round((end - start) / 60_000)));
}

/** Search text for the incident grid's built-in global filter. */
export function incidentSearchText(incident: M365Incident): string {
  return [incident.id, incident.title, incident.service, incident.classification, incident.status]
    .filter(Boolean)
    .join(" ");
}
